
import { BrowserRouter, Routes, Route, Outlet } from "react-router-dom";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as SonnerToaster } from "sonner";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { TooltipProvider } from "@/components/ui/tooltip";

import LandingPage from "@/pages/LandingPage";
import Index from "@/pages/Index";
import FormPage from "@/pages/FormPage";
import Reports from "@/pages/Reports";
import TeacherReportsPage from "@/pages/TeacherReportsPage";
import ReportDetail from "@/pages/ReportDetail";
import EditReportPage from "@/pages/EditReportPage";
import AdminEditRequestsPage from "@/pages/AdminEditRequestsPage";
import Auth from "@/pages/Auth";
import AuthCallback from "@/pages/AuthCallback";
import Profile from "@/pages/Profile";
import NotFound from "@/pages/NotFound";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminWorksheetCheck from "@/pages/AdminWorksheetCheck";
import AdminWorksheetManagementPage from "@/pages/AdminWorksheetManagementPage";
import AdminLogs from "@/pages/AdminLogs";
import TeacherSalaryPage from "@/pages/TeacherSalaryPage";
import DeprecatedFeaturePage from "@/pages/DeprecatedFeaturePage";
import TeacherSchedulePage from "@/pages/TeacherSchedulePage";
import AdminStudentListPage from "@/pages/AdminStudentListPage";
import AdminAddStudentPage from "@/pages/AdminAddStudentPage";
import AdminTeacherProfilesPage from "@/pages/AdminTeacherProfilesPage";
import AdminTeacherDetailPage from "@/pages/AdminTeacherDetailPage";
import WorksheetSearchPage from "@/pages/WorksheetSearchPage";
import AdminOrderOverviewPage from "@/pages/AdminOrderOverviewPage";
import AdminMonthlySnapshotPage from "@/pages/AdminMonthlySnapshotPage";
import AdminDailySnapshotPage from "@/pages/AdminDailySnapshotPage";
import AdminClassTrialsPage from "@/pages/AdminClassTrialsPage";
import AdminClassTrialEditPage from "@/pages/AdminClassTrialEditPage";
import AdminClassTrialStatsPage from "@/pages/AdminClassTrialStatsPage";
import AdminSuddenLeavePage from "@/pages/AdminSuddenLeavePage";
import AdminClassTemplatesPage from "@/pages/AdminClassTemplatesPage";
import AdminCreateClassTemplatePage from "@/pages/AdminCreateClassTemplatePage";
import AdminProtectedRoute from "@/components/auth/AdminProtectedRoute";
import { AuthProvider } from "@/context/auth/AuthProvider";
import MainLayout from "@/components/layout/MainLayout";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import TeacherSalary from "@/pages/TeacherSalary";
import AdminSalaryVerificationPage from "@/pages/AdminSalaryVerificationPage";
import AdminTeacherPayrollPage from "@/pages/AdminTeacherPayrollPage";
import AdminCommissionsPage from "@/pages/AdminCommissionsPage";
import AdminTeacherStudentsPage from "@/pages/AdminTeacherStudentsPage";
import AdminTeacherStatsPage from "@/pages/AdminTeacherStatsPage";
import AdminDepletionStatusPage from "@/pages/AdminDepletionStatusPage";
import AdminRegularStudentsPage from "@/pages/AdminRegularStudentsPage";
import AdminTeacherMatchPage from "@/pages/AdminTeacherMatchPage";
import AdminTeacherEditPage from "@/pages/AdminTeacherEditPage";
import AdminParentReviewsPage from "@/pages/AdminParentReviewsPage";
import TeacherTrialsPage from "@/pages/TeacherTrialsPage";
import TeacherTrialEvalPage from "@/pages/TeacherTrialEvalPage";
import PublicTrialFormPage from "@/pages/PublicTrialFormPage";
import EventsPage from "@/pages/EventsPage";
import StudentEventBalancesPage from "@/pages/StudentEventBalancesPage";
import TeacherLineLinkPage from "@/pages/TeacherLineLinkPage";
import TeacherMatchRespondPage from "@/pages/TeacherMatchRespondPage";
const queryClient = new QueryClient();

function App() {
  return (
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <TooltipProvider>
            <Routes>
              <Route path="/auth" element={<Auth />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/trial-form" element={<PublicTrialFormPage />} />
              <Route path="/teacher-line-link" element={<TeacherLineLinkPage />} />
              <Route element={<MainLayout />}>
                <Route element={<ProtectedRoute />}>
                  <Route path="/" element={<LandingPage />} />
                  <Route path="/teacher-reports" element={<Reports />} />
                  <Route path="/form" element={<FormPage />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/reports/:id" element={<ReportDetail />} />
                  <Route path="/reports/:id/edit" element={<EditReportPage />} />
                  <Route path="/salary" element={<TeacherSalaryPage />} />
                  <Route path="/special-class" element={<DeprecatedFeaturePage feature="special-class" />} />
                  <Route path="/special-class/form" element={<DeprecatedFeaturePage feature="special-class" />} />
                  <Route path="/teacher-schedule" element={<TeacherSchedulePage />} />
                  <Route path="/worksheet-search" element={<WorksheetSearchPage />} />
                  <Route path="/teacher-trials" element={<TeacherTrialsPage />} />
                  <Route path="/teacher-trials/:id" element={<TeacherTrialEvalPage />} />
                  <Route path="/teacher-match-respond/:requestId" element={<TeacherMatchRespondPage />} />
                </Route>
              </Route>
              <Route path="/public-form" element={<Index />} />
              <Route path="/form" element={<FormPage />} />
              <Route path="/reports" element={<TeacherReportsPage />} />
              <Route path="/reports/:id" element={<ReportDetail />} />
              
              <Route path="/profile" element={<Profile />} />
              {/* Admin Routes */}
              <Route element={<AdminProtectedRoute />}>
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/admin/monthly-snapshot" element={<AdminMonthlySnapshotPage />} />
                <Route path="/admin/daily-snapshot" element={<AdminDailySnapshotPage />} />
                <Route path="/admin/worksheet-check" element={<AdminWorksheetCheck />} />
                <Route path="/admin/worksheet-management" element={<AdminWorksheetManagementPage />} />
                <Route path="/admin/logs" element={<AdminLogs />} />
                <Route path="/admin/students" element={<AdminStudentListPage />} />
                <Route path="/admin/students/add" element={<AdminAddStudentPage />} />
                <Route path="/admin/order-overview" element={<AdminOrderOverviewPage />} />
                <Route path="/admin/teachers" element={<AdminTeacherProfilesPage />} />
                <Route path="/admin/teachers/:teacherCode" element={<AdminTeacherDetailPage />} />
                <Route path="/admin/teachers/:teacherCode/edit" element={<AdminTeacherEditPage />} />
                <Route path="/admin/teacher-salary" element={<TeacherSalary />} />
                <Route path="/admin/teacher-payroll" element={<AdminTeacherPayrollPage />} />
                <Route path="/admin/commissions" element={<AdminCommissionsPage />} />
                <Route path="/admin/teacher-students" element={<AdminTeacherStudentsPage />} />
                <Route path="/admin/teacher-stats" element={<AdminTeacherStatsPage />} />
                <Route path="/admin/salary-verification" element={<AdminSalaryVerificationPage />} />
                <Route path="/admin/report-edit-requests" element={<AdminEditRequestsPage />} />
                <Route path="/admin/class-trials" element={<AdminClassTrialsPage />} />
                <Route path="/admin/class-trials/:id/edit" element={<AdminClassTrialEditPage />} />
                <Route path="/admin/class-trials-stats" element={<AdminClassTrialStatsPage />} />
                <Route path="/admin/sudden-leave" element={<AdminSuddenLeavePage />} />
                <Route path="/admin/jobs" element={<DeprecatedFeaturePage feature="fixed-jobs" />} />
                <Route path="/admin/occurrence-edits" element={<DeprecatedFeaturePage feature="occurrence-edits" />} />
                <Route path="/admin/class-templates" element={<AdminClassTemplatesPage />} />
                <Route path="/admin/class-templates/create" element={<AdminCreateClassTemplatePage />} />
                <Route path="/admin/depletion-status" element={<AdminDepletionStatusPage />} />
                <Route path="/admin/regular-students" element={<AdminRegularStudentsPage />} />
                <Route path="/admin/teacher-match" element={<AdminTeacherMatchPage />} />
                <Route path="/admin/parent-reviews" element={<AdminParentReviewsPage />} />
                <Route path="/admin/events" element={<EventsPage />} />
                <Route path="/admin/student-event-balances" element={<StudentEventBalancesPage />} />
              </Route>
              <Route path="*" element={<NotFound />} />
            </Routes>
            <Toaster />
            <SonnerToaster position="top-right" richColors />
          </TooltipProvider>
        </AuthProvider>
      </QueryClientProvider>
    </BrowserRouter>
  );
}

export default App;
